import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState(null);

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      
      // Check if must change password from token
      const tokenData = parseJwt(token);
      setMustChangePassword(tokenData?.must_change_password || false);
    } catch (error) {
      console.error('Failed to load user', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  };

  const login = async (email, password, totpCode = null) => {
    try {
      const payload = { email, password };
      if (totpCode) {
        payload.totp_code = totpCode;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, payload);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setPendingCredentials({ email, password });
        return { success: false, requires2FA: true };
      }
      
      const { access_token, user: userData, must_change_password } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      setMustChangePassword(must_change_password || false);
      setRequires2FA(false);
      setPendingCredentials(null);
      
      return { success: true, mustChangePassword: must_change_password };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const verify2FA = async (code) => {
    if (!pendingCredentials) {
      return { success: false, error: 'No pending login' };
    }
    return await login(pendingCredentials.email, pendingCredentials.password, code);
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setPendingCredentials(null);
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMustChangePassword(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Password change failed'
      };
    }
  };

  const forceChangePassword = async (newPassword) => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/force-change-password`, {
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMustChangePassword(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Password change failed'
      };
    }
  };

  const logout = async () => {
    // Révoque le token côté serveur (best-effort : si réseau down, on déconnecte quand même)
    try {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        await axios.post(
          `${API_BASE_URL}/api/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${currentToken}` } }
        );
      }
    } catch (_) {
      // Ignore les erreurs réseau — la déconnexion locale se fait quand même
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setRequires2FA(false);
    setPendingCredentials(null);
  };


  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token && !!user,
    mustChangePassword,
    changePassword,
    forceChangePassword,
    requires2FA,
    verify2FA,
    cancel2FA
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
